import { createContext, useContext, useState, useEffect } from "react";
import { db, auth } from "../firebase/config";
import { collection, doc, getDoc, getDocs, query, setDoc, serverTimestamp, updateDoc, where } from "firebase/firestore";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail,
} from "firebase/auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const saved = localStorage.getItem("bskr_user");
        if (saved) {
          const parsed = JSON.parse(saved);
          setUser(parsed);
        }
      } catch {
        localStorage.removeItem("bskr_user");
      }

      setLoading(false);
    };

    restoreSession();
  }, []);

  async function ensurePlatformUserProfile(firebaseUser) {
    const email = (firebaseUser.email || "").trim().toLowerCase();
    const usersRef = collection(db, "platformUsers");
    const snapshot = await getDocs(usersRef);

    if (snapshot.empty) {
      const uid = firebaseUser.uid;
      const fallbackName = email ? email.split("@")[0] : "Platform User";
      const initialProfile = {
        name: firebaseUser.displayName || fallbackName,
        email,
        mobile: "",
        role: "Owner",
        status: "Active",
        createdOn: serverTimestamp(),
        updatedOn: serverTimestamp(),
      };

      await setDoc(doc(db, "platformUsers", uid), initialProfile);
      return { profile: initialProfile, created: true };
    }

    let profileDoc = null;
    if (email) {
      const q = query(usersRef, where("email", "==", email));
      const matchingSnapshot = await getDocs(q);
      if (!matchingSnapshot.empty) {
        profileDoc = matchingSnapshot.docs[0];
      }
    }

    if (!profileDoc) {
      const fallbackDoc = await getDoc(doc(db, "platformUsers", firebaseUser.uid));
      if (fallbackDoc.exists()) {
        profileDoc = fallbackDoc;
      }
    }

    if (!profileDoc || !profileDoc.exists()) {
      return { profile: null, created: false, deniedReason: "missing" };
    }

    const profile = profileDoc.data();
    if (profile.status === "PendingActivation") {
      // First time this person has set their password and logged in —
      // mark their account Active.
      await updateDoc(doc(db, "platformUsers", profileDoc.id), {
        status: "Active",
        updatedOn: serverTimestamp(),
      });
      profile.status = "Active";
      return { profile, created: false };
    }

    if (profile.status !== "Active") {
      return { profile: null, created: false, deniedReason: "inactive" };
    }

    return { profile, created: false };
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        localStorage.removeItem("bskr_user");
        setLoading(false);
        return;
      }

      try {
        const { profile, created } = await ensurePlatformUserProfile(firebaseUser);

        if (!profile) {
          await signOut(auth);
          setUser(null);
          localStorage.removeItem("bskr_user");
          setLoading(false);
          return;
        }

        const sessionUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: profile.name || firebaseUser.displayName || "Platform User",
          role: profile.role || "User",
          status: profile.status,
          ...profile,
        };

        if (created) {
          sessionUser.createdOn = profile.createdOn;
        }

        setUser(sessionUser);
        localStorage.setItem("bskr_user", JSON.stringify(sessionUser));
      } catch (error) {
        console.error("Auth profile load failed", error);
        await signOut(auth);
        setUser(null);
        localStorage.removeItem("bskr_user");
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  function getFriendlyAuthError(error) {
    const code = error?.code;
    switch (code) {
      case "auth/invalid-email":
        return "Invalid email";
      case "auth/wrong-password":
      case "auth/invalid-login-credentials":
      case "auth/invalid-credential":
        return "Invalid password";
      case "auth/user-not-found":
        return "User not found";
      case "auth/user-disabled":
        return "Account disabled";
      case "auth/too-many-requests":
        return "Too many sign-in attempts. Please try again shortly.";
      default:
        return error?.message || "Unable to sign in. Please try again.";
    }
  }

  async function login(email, password) {
    try {
      await setPersistence(auth, browserLocalPersistence);
      const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const { profile, created, deniedReason } = await ensurePlatformUserProfile(credential.user);

      if (!profile) {
        await signOut(auth);
        return {
          success: false,
          error:
            deniedReason === "pending"
              ? "Account is pending activation. Please use Activate Account."
              : deniedReason === "inactive"
              ? "Account inactive"
              : "Access denied (platformUsers document missing)",
        };
      }

      const sessionUser = {
        uid: credential.user.uid,
        email: credential.user.email,
        name: profile.name || credential.user.displayName || "Platform User",
        role: profile.role || "User",
        status: profile.status,
        ...profile,
      };

      if (created) {
        sessionUser.createdOn = profile.createdOn;
      }

      setUser(sessionUser);
      localStorage.setItem("bskr_user", JSON.stringify(sessionUser));
      return { success: true, user: sessionUser };
    } catch (error) {
      return { success: false, error: getFriendlyAuthError(error) };
    }
  }

  async function forgotPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email.trim());
      return { success: true };
    } catch (error) {
      return { success: false, error: getFriendlyAuthError(error) };
    }
  }

  async function logout() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      setUser(null);
      localStorage.removeItem("bskr_user");
    }
  }

  // ── Role helpers ───────────────────────────────────────────
  const isAdyantam   = () => user?.role === "Adyantam";
  const isPresident  = () => user?.role === "President";
  const isSecretary  = () => user?.role === "Secretary";
  const isTreasurer  = () => user?.role === "Treasurer";
  const isWatchman   = () => user?.role === "Watchman";
  const isMC         = () => ["President", "Secretary", "Treasurer", "Adyantam"].includes(user?.role);
  const isFlatOwner  = () => user?.role === "FlatOwner";
  const isTenant     = () => user?.memberType === "tenant";
  const isFamilyMember = () => user?.memberType === "family";
  const isSalesHead   = () => user?.role === "SalesHead";

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        forgotPassword,
        logout,
        isAdyantam,
        isPresident,
        isSecretary,
        isTreasurer,
        isWatchman,
        isMC,
        isFlatOwner,
        isTenant,
        isFamilyMember,
        isSalesHead,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
